import { formatTimeSpan, mapLookup, displayErrors } from "common/helpers";
import { formatDurationWithDays } from "common/dateTimeUtils";
import {
    taskSvc as service,
    lookupSvc,
    dashboardsService as dshbSvc,
    dashboardGroupsService as dshbGrSvc,
    snowflakeLoadConfigService
} from "services/serviceStorage";
import dayjs from "dayjs";
import {
    FetchTaskEditLookupsParams,
    TaskCopyLookups,
    DashboardsLookup,
    SaveMarketModel,
    ReleaseLookupItem,
    MarketModelLookupItem,
    TaskEditLookups,
    ReportTemplateLookupItem,
    TaskCopyModalSource,
    DatasetConvertTask,
    TaskContract
} from './types';
import { LookupItem, BaseApiResponse, Task } from "common/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Dashboard, DashboardGroup } from "store/dashboardViewer/types";
import { wrapRequest } from "common/utils";
import { LoadConfig } from "store/staticReportsSnowflake/types";
import type { RootState } from "store";

export const fetchTaskEditLookups = createAsyncThunk<TaskEditLookups, FetchTaskEditLookupsParams>(
    "taskMgmt/fetchTaskEditLookups",
    async ({ taskId, releaseId, releaseGroupId, allowChangeRelease }, { rejectWithValue }) => {
        try {
            const requests = [
                lookupSvc.getPartitioningModes(),
                lookupSvc.getTimeUnits(),
                lookupSvc.getReleases(allowChangeRelease ? undefined : taskId),
                lookupSvc.getMarketModels(releaseId || ""),
                lookupSvc.getCubeTemplates(taskId),
                lookupSvc.getReportTemplates(taskId),
                lookupSvc.getVMGroups(),
                service.getLeadsLookup(),
            ];

            if (releaseGroupId) {
                requests.push(
                    dshbSvc.getDashboardsList({ releaseGroupIds: [releaseGroupId] }),
                    dshbGrSvc.getDashboardGroupsList({ releaseGroupIds: [releaseGroupId] }),
                    snowflakeLoadConfigService.list({ releaseGroupIds: [releaseGroupId], includeGlobal: true, ...(taskId !== null && { taskIds: [taskId] }) })
                );
            }

            const [
                partitioningRes,
                timeUnitRes,
                releasesRes,
                marketModelsRes,
                cubeTemplatesRes,
                reportTemplatesRes,
                vmGroupsRes,
                leadsRes,
                dashboardsRes,
                dashboardGroupsRes,
                loadConfigRes
            ] = await Promise.all(requests);

            return {
                partitioning: partitioningRes.data.result.map(mapLookup),
                timeUnits: timeUnitRes.data.result.map(mapLookup),
                releases: releasesRes.data.result.map((r: ReleaseLookupItem) => ({
                    ...r,
                    key: r.id,
                    displayName: r.displayName,
                    releaseGroupId: r.releaseGroupId,
                })),
                marketModels: marketModelsRes.data.result.map(mapLookup),
                cubeTemplates: cubeTemplatesRes.data.result.map(mapLookup),
                reportTemplates: reportTemplatesRes.data.result.map((r: ReportTemplateLookupItem) => ({
                    ...r,
                    key: r.id,
                    displayName: r.displayName,
                    releaseGroupId: r.releaseGroupId,
                    isGlobal: r.isGlobal,
                })),
                vmGroups: vmGroupsRes.data.result.map(mapLookup),
                leads: leadsRes.data.result.map(mapLookup),
                dashboards: dashboardsRes?.data.result.items.map((d: Dashboard) => ({
                    value: d.dashboardId,
                    label: d.dashboardName,
                })) ?? [],
                dashboardGroups: dashboardGroupsRes?.data.result.items.map((g: DashboardGroup) => ({
                    value: g.groupId,
                    label: g.groupName,
                    isGroup: true,
                    source: g.dashboards.map(d => d.id),
                })) ?? [],
                loadConfigs: loadConfigRes?.data.result.items.map((c: LoadConfig) => ({
                    value: c.id,
                    label: c.name,
                })) ?? []
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error);
        }
    }
);

export const fetchTaskCopyLookups = createAsyncThunk<TaskCopyLookups, { [key: string]: number }>(
    "taskMgmt/fetchTaskCopyLookups",
    async ({ taskId, releaseGroupId }, { rejectWithValue }) => {
        try {
            const [projectsRes, scenariosRes, releasesRes, marketModelsRes, releaseGroupsRes] = await Promise.all([
                lookupSvc.getProjects(),
                service.getScenariosLookup(taskId),
                lookupSvc.getReleases(),
                lookupSvc.getMarketModelsV2(),
                lookupSvc.getTaskReleaseGroups(releaseGroupId)
            ]);

            return {
                projects: projectsRes.data.result.map(mapLookup),
                scenarios: scenariosRes.data.result.map((r: LookupItem) => ({
                    idx: r.id,
                    displayName: `${r.id} ${r.displayName}`,
                })),
                releases: releasesRes.data.result.map((r: ReleaseLookupItem) => ({
                    ...r,
                    key: r.id,
                    id: r.id,
                    displayName: r.displayName,
                    releaseGroupId: r.releaseGroupId,
                })),
                marketModels: marketModelsRes.data.result.items.map((mm: MarketModelLookupItem) => ({
                    key: mm.id,
                    id: mm.id,
                    displayName: mm.name,
                })),
                releaseGroups: releaseGroupsRes.data.result,
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error)
        }
    }
);

export const fetchTasks = createAsyncThunk<{ data: Task[]; total: number }, void, { state: RootState }>(
    'scenarioMgmt/fetchTasks',
    async (_, { getState }) => {
        const { filter } = getState().taskMgmtSlice.gridSource;

        if (!filter.activeStates.length) {
            return { data: [], total: 0 };
        }

        const { data } = await wrapRequest<BaseApiResponse<{ items: Task[]; totalCount: number }>>({
            handler: () => service.getTasks(filter),
            showNotifications: true
        });

        const mappedTasks = {
            data: data?.result.items.map(t => ({
                ...t,
                key: t.taskId,
                taskId: t.taskId,
                taskName: t.taskName,
                psoTime: typeof t.psoTime === 'number' && t.psoTime > 0 ? formatDurationWithDays(t.psoTime) : '',
                vmTime: typeof t.vmTime === 'number' && t.vmTime > 0 ? formatDurationWithDays(t.vmTime) : '',
                leadName: t.leadName,
                isActive: t.isActive,
                lastRunDate: t.lastRunDate && dayjs.utc(t.lastRunDate).local().format("MM-DD-YYYY HH:mm"),
                marketModel: t.marketModel,
                instanceId: t.instanceId,
                archiveType: t.archiveType,
                archiveDays: t.archiveDays,
                activeState: t.activeState,
                activeStateNextChangePeriod: formatTimeSpan(t.activeStateNextChangePeriod),
                versionId: t.versionId,
            })) || [],
            total: data?.result.totalCount || 0,
        }

        return mappedTasks;
    }
);

export const fetchTask = createAsyncThunk<Task, number>(
    'taskMgmt/fetchTask',
    async (taskId) => {
        const { data } = await wrapRequest<BaseApiResponse<Task>>({
            handler: () => service.getTask(taskId),
            showNotifications: true
        });

        return data!.result;
    }
);

export const deleteTask = createAsyncThunk<void, number>(
    'taskMgmt/deleteTask',
    async (taskId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.deleteTask(taskId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const forceDeleteTask = createAsyncThunk<void, number>(
    'taskMgmt/forceDeleteTask',
    async (taskId) => {
        await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.forceDeleteTask(taskId),
            showNotifications: true
        });
    }
);

export const saveTask = createAsyncThunk<void, Task>(
    'taskMgmt/saveTask',
    async (task, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.putTask({
                ...task,
                startDate: task.startDate && dayjs(task.startDate).format('YYYY-MM-DDTHH:mm:ss'), // Ignore timezone offset and send local date as UTC+0
                endDate: task.endDate && dayjs(task.endDate).format('YYYY-MM-DDTHH:mm:ss'),
                minDate: task.minDate && dayjs(task.minDate).format('YYYY-MM-DDTHH:mm:ss'),
                maxDate: task.maxDate && dayjs(task.maxDate).format('YYYY-MM-DDTHH:mm:ss'),
            } as TaskContract),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const archiveTask = createAsyncThunk<void, number>(
    'taskMgmt/archiveTask',
    async (taskId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.archiveTask(taskId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const copyTask = createAsyncThunk<void, TaskCopyModalSource>(
    'taskMgmt/copyTask',
    async (task, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.copyTask(task),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const datasetConvert = createAsyncThunk<void, DatasetConvertTask>(
    'taskMgmt/datasetConvert',
    async (task, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.datasetConvert(task),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const initializeDb = createAsyncThunk<void, number>(
    'taskMgmt/initializeDb',
    async (taskId) => {
        await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.initDb(taskId),
            showNotifications: true
        });
    }
);

export const updateDUPSRelease = createAsyncThunk<void, number>(
    'taskMgmt/updateDUPSRelease',
    async (taskId) => {
        await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.updateDUPSRelease(taskId),
            showNotifications: true
        });
    }
);

export const fetchDashboardLookups = createAsyncThunk<DashboardsLookup, number>(
    "taskMgmt/fetchDashboardLookups",
    async (releaseGroupId, { rejectWithValue }) => {
        try {
            const [dashboardsRes, dashboardGroupsRes, loadConfigRes] = await Promise.all([
                dshbSvc.getDashboardsList({ releaseGroupIds: [releaseGroupId] }),
                dshbGrSvc.getDashboardGroupsList({ releaseGroupIds: [releaseGroupId] }),
                snowflakeLoadConfigService.list({ releaseGroupIds: [releaseGroupId], includeGlobal: true })
            ]);

            return {
                dashboards: dashboardsRes?.data.result.items.map((d: Dashboard) => ({
                    value: d.dashboardId,
                    label: d.dashboardName,
                })) ?? [],
                dashboardGroups: dashboardGroupsRes?.data.result.items.map((g: DashboardGroup) => ({
                    value: g.groupId,
                    label: g.groupName,
                    isGroup: true,
                    source: g.dashboards.map(d => d.id),
                })) ?? [],
                loadConfigs: loadConfigRes?.data.result.items.map((c: LoadConfig) => ({
                    value: c.id,
                    label: c.name,
                })) ?? []
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error)
        }
    }
);

export const saveMarketModel = createAsyncThunk<void, SaveMarketModel>(
    'taskMgmt/saveMarketModel',
    async (model, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.createMarketModel(model),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const archiveAndDeleteDb = createAsyncThunk<void, number>(
    'taskMgmt/archiveAndDeleteDb',
    async (taskId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.archiveAndDeleteDb(taskId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        }
    }
);

export const updateMajorRelease = createAsyncThunk<void, number>(
    'taskMgmt/updateMajorRelease',
    async (taskId) => {
        await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.updateMajorRelease(taskId),
            showNotifications: true
        });
    }
);